import { memo, useMemo } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { DCOLS, ENV_STYLE, monoFont, tagSx } from './DiConstants';

/* ─── Shared primitives ──────────────────────────────────────────────────── */
function SectionHead({ label, title, desc }) {
    return (
        <Box sx={{ mb: 2.25 }}>
            {label && (
                <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px', color: '#9ca3af', mb: 0.5 }}>
                    {label}
                </Typography>
            )}
            <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.4px', color: '#111827' }}>
                {title}
            </Typography>
            {desc && <Typography sx={{ fontSize: 13, color: '#374151', mt: 0.5 }}>{desc}</Typography>}
        </Box>
    );
}

const ACCENT_COLOR = {
    's-green': '#059669', 's-amber': '#d97706', 's-red': '#dc2626', 's-purple': '#7c3aed',
};

const StatCard = memo(function StatCard({ label, value, sub, accent }) {
    const color = ACCENT_COLOR[accent] || '#e8eaef';
    return (
        <Paper sx={{ borderRadius: '12px', p: '20px 18px', position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, backgroundColor: color, borderRadius: '12px 12px 0 0' }} />
            <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.25 }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: 30, fontWeight: 700, lineHeight: 1, letterSpacing: '-1px', color: ACCENT_COLOR[accent] || '#111827' }}>
                {value}
            </Typography>
            {sub && <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.75 }}>{sub}</Typography>}
        </Paper>
    );
});

const BarChartCard = memo(function BarChartCard({ title, data }) {
    const entries = useMemo(
        () => Object.entries(data || {}).sort((a, b) => b[1] - a[1]),
        [data],
    );
    const mx = useMemo(() => Math.max(...entries.map(([, v]) => v), 1), [entries]);
    return (
        <Paper sx={{ borderRadius: '12px', p: '20px' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#9ca3af', mb: 2 }}>
                {title}
            </Typography>
            {entries.map(([name, count]) => {
                const pct = count / mx;
                return (
                    <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, '&:last-child': { mb: 0 } }}>
                        <Tooltip title={name} placement="top-start">
                            <Typography sx={{ fontSize: 12, color: '#374151', width: 80, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0 }}>
                                {name}
                            </Typography>
                        </Tooltip>
                        <Box sx={{ flex: 1, height: 5, backgroundColor: '#f3f4f6', borderRadius: 1.5, overflow: 'hidden' }}>
                            <Box sx={{ height: '100%', backgroundColor: '#111827', borderRadius: 1.5, width: `${pct * 100}%`, opacity: 0.25 + 0.75 * pct }} />
                        </Box>
                        <Typography sx={{ fontFamily: monoFont, fontSize: 11, color: '#9ca3af', width: 24, textAlign: 'right', flexShrink: 0 }}>
                            {count}
                        </Typography>
                    </Box>
                );
            })}
        </Paper>
    );
});

const PlatformMatrix = memo(function PlatformMatrix({ modules, platforms, matrixRows, matTop }) {
    if (!modules.length || !platforms.length) {
        return <Typography sx={{ p: 2.5, fontSize: 13, color: '#9ca3af' }}>Not enough data.</Typography>;
    }
    const cellSx = (v) => {
        if (v === 0) return { color: '#9ca3af' };
        const r = v / matTop;
        if (r < 0.25) return { backgroundColor: '#ecfdf5', color: '#059669' };
        if (r < 0.60) return { backgroundColor: '#fffbeb', color: '#d97706' };
        return { backgroundColor: '#fef2f2', color: '#dc2626' };
    };
    return (
        <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 500, borderCollapse: 'collapse' }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ textAlign: 'left !important', width: 160 }}>Module</TableCell>
                        {platforms.map(p => <TableCell key={p} sx={{ textAlign: 'center !important' }}>{p}</TableCell>)}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {modules.map((mod, mi) => {
                        const rowVals = Object.values(matrixRows[mi] || {});
                        return (
                            <TableRow key={mod} sx={{ '&:hover td': { backgroundColor: '#fafafa' } }}>
                                <TableCell sx={{ fontWeight: 600, fontSize: 13, color: '#111827', backgroundColor: '#f3f4f6', '&:hover': { backgroundColor: '#f3f4f6 !important' } }}>
                                    {mod}
                                </TableCell>
                                {platforms.map((pl, pi) => {
                                    const v = rowVals[pi] || 0;
                                    return (
                                        <TableCell key={pl} sx={{ textAlign: 'center', borderBottom: '1px solid #e8eaef' }}>
                                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 32, height: 24, borderRadius: '5px', fontFamily: monoFont, fontSize: 12, fontWeight: 500, ...cellSx(v) }}>
                                                {v || '—'}
                                            </Box>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </Box>
    );
});

const EnvCoverage = memo(function EnvCoverage({ modules, envRows, flagValues }) {
    return (
        <Grid container spacing={1.25}>
            {modules.map((mod, mi) => {
                const flag = flagValues[mi];
                const envs = envRows[mi] || {};
                const cardBorder = flag === 'non-production-only' ? '#fde68a' : flag === 'no-coverage' ? '#fecaca' : '#e8eaef';
                const cardBg = flag === 'non-production-only' ? '#fffbeb' : flag === 'no-coverage' ? '#fef2f2' : '#ffffff';
                const badge = flag === 'has-production'
                    ? <Box component="span" sx={tagSx('#ecfdf5', '#059669')}>✓ production</Box>
                    : flag === 'non-production-only'
                        ? <Box component="span" sx={tagSx('#fffbeb', '#d97706')}>non-prod only</Box>
                        : <Box component="span" sx={tagSx('#fef2f2', '#dc2626')}>no data</Box>;
                return (
                    <Grid item xs={12} sm={6} md={4} key={mod}>
                        <Paper sx={{ borderRadius: '12px', p: 2, borderColor: cardBorder, backgroundColor: cardBg }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.25 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{mod}</Typography>
                                {badge}
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                {Object.entries(envs).sort((a, b) => b[1] - a[1]).map(([env, cnt]) => {
                                    const s = ENV_STYLE[env] || { bg: '#f3f4f6', color: '#9ca3af' };
                                    const label = env.charAt(0).toUpperCase() + env.slice(1).replace('_', ' ');
                                    return <Box key={env} component="span" sx={tagSx(s.bg, s.color)}>{label} {cnt}</Box>;
                                })}
                                {!Object.keys(envs).length && <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>no classified issues</Typography>}
                            </Box>
                        </Paper>
                    </Grid>
                );
            })}
        </Grid>
    );
});

const SprintTable = memo(function SprintTable({ sprintRanking }) {
    if (!sprintRanking.length) {
        return <Typography sx={{ p: 3, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No production defects in this dataset.</Typography>;
    }
    const mx = sprintRanking[0].count;
    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell sx={{ width: 32 }}></TableCell>
                    <TableCell>Module · Platform</TableCell>
                    <TableCell>Production defects</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {sprintRanking.map((row, i) => {
                    const bw = Math.max(4, Math.round((row.count / mx) * 160));
                    return (
                        <TableRow key={row.combo} sx={{ '&:hover td': { backgroundColor: '#f3f4f6' } }}>
                            <TableCell sx={{ fontFamily: monoFont, fontSize: 12, color: '#9ca3af' }}>{i + 1}</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#111827' }}>{row.combo}</TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                    <Box sx={{ height: 6, borderRadius: 1.5, backgroundColor: '#dc2626', opacity: 0.7, minWidth: 4, width: bw }} />
                                    <Typography sx={{ fontFamily: monoFont, fontSize: 12, color: '#9ca3af', minWidth: 20 }}>{row.count}</Typography>
                                </Box>
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
});

/* ═══════════════════════════════════════════════════════════════════════════
   INSIGHTS TAB
   memo + useMemo: only re-renders when `ins` reference changes (i.e. new upload)
═══════════════════════════════════════════════════════════════════════════ */
export const InsightsTab = memo(function InsightsTab({ ins }) {
    const stats = useMemo(() => [
        { l: 'Total Issues', v: ins.total, accent: '', s: 'evaluated' },
        // { l: 'Free (no API)', v: ins.classified_by_keyword, accent: 's-green', s: 'keyword + pipe' },
        // { l: 'AI Fallback', v: ins.classified_by_l_l_m, accent: 's-purple', s: 'Gemini classified' },
        { l: 'Prod Bugs', v: ins.production_bugs, accent: 's-red', s: 'defects in production' },
        { l: 'Critical', v: ins.critical_issues, accent: 's-red', s: 'issues with high priority' },
        { l: 'Dup Clusters', v: ins.duplicate_groups, accent: 's-amber', s: `${ins.duplicate_issues} issues` },
        { l: 'Unresolved', v: ins.unresolved_fields, accent: 's-amber', s: 'fields still unclassified' },
    ], [ins]);

    const charts = useMemo(() => [
        { t: 'Module', d: ins.by_module },
        { t: 'Platform', d: ins.by_platform },
        { t: 'Environment', d: ins.by_environment },
        { t: 'Country', d: ins.by_country },
    ], [ins]);

    const { modules = [], platforms = [], platform_matrix = {}, env_coverage = {}, env_coverage_flags = {}, sprint_ranking = [] } = ins;

    const { matrixRows, envRows, flagValues, matTop } = useMemo(() => {
        const matrixRows = Object.values(platform_matrix);
        const envRows = Object.values(env_coverage);
        const flagValues = Object.values(env_coverage_flags);
        const allV = matrixRows.flatMap(row => Object.values(row || {}));
        return { matrixRows, envRows, flagValues, matTop: Math.max(...allV, 1) };
    }, [platform_matrix, env_coverage, env_coverage_flags]);

    return (
        <Box>
            <Box sx={{ mb: 5 }}>
                <SectionHead label="Overview" title="Classification summary" />
                <Grid container spacing={1.5}>
                    {stats.map(s => (
                        <Grid item xs={6} sm={4} md={3} key={s.l} sx={{ minWidth: 140 }}>
                            <StatCard label={s.l} value={s.v} sub={s.s} accent={s.accent} />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Divider sx={{ my: 5 }} />

            <Box sx={{ mb: 5 }}>
                <SectionHead title="Distribution" />
                <Grid container spacing={1.5}>
                    {charts.map(c => (
                        <Grid item xs={12} sm={6} md={3} key={c.t}>
                            <BarChartCard title={c.t} data={c.d} />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Divider sx={{ my: 5 }} />

            <Box sx={{ mb: 5 }}>
                <SectionHead label="Coverage" title="Platform coverage matrix" />
                <Paper sx={{ borderRadius: '12px', overflow: 'hidden' }}>
                    <PlatformMatrix modules={modules} platforms={platforms} matrixRows={matrixRows} matTop={matTop} />
                </Paper>
            </Box>

            <Divider sx={{ my: 5 }} />

            <Box sx={{ mb: 5 }}>
                <SectionHead label="Coverage" title="Environment coverage" desc="Modules flagged non-prod only have defects in Dev/Staging but never Production." />
                <EnvCoverage modules={modules} envRows={envRows} flagValues={flagValues} />
            </Box>

            <Divider sx={{ my: 5 }} />

            <Box sx={{ mb: 5 }}>
                <SectionHead label="Planning" title="Sprint planning signal" desc="Production defects by module and platform, ranked by count." />
                <Paper sx={{ borderRadius: '12px', overflow: 'hidden' }}>
                    <SprintTable sprintRanking={sprint_ranking} />
                </Paper>
            </Box>
        </Box>
    );
});

/* ═══════════════════════════════════════════════════════════════════════════
   DUPES TAB
   memo: only re-renders when dups/classified changes (i.e. new upload)
═══════════════════════════════════════════════════════════════════════════ */
export const DupesTab = memo(function DupesTab({ dups, classified }) {
    if (!dups?.length) {
        return <Typography sx={{ color: '#9ca3af', fontSize: 13, pt: 1 }}>No duplicate clusters detected.</Typography>;
    }
    return (
        <Box>
            <SectionHead label="Duplicates" title="Similar issue clusters" desc="Issues grouped by text similarity within the same module and platform." />
            <Grid container spacing={1.5}>
                {dups.map((grp, gi) => {
                    const col = DCOLS[gi % DCOLS.length];
                    return (
                        <Grid item xs={12} md={6} key={gi}>
                            <Paper sx={{ borderRadius: '12px', overflow: 'hidden' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5, backgroundColor: '#f3f4f6', borderBottom: '1px solid #e8eaef' }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: col, flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>Cluster {gi + 1}</Typography>
                                    <Typography sx={{ fontSize: 11, color: '#9ca3af', fontFamily: monoFont, ml: 'auto' }}>{grp.length} issues</Typography>
                                </Box>
                                {grp.map((idx, j) => (
                                    <Box key={idx} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', px: 2, py: 1.125, borderBottom: j < grp.length - 1 ? '1px solid #e8eaef' : 'none' }}>
                                        <Typography sx={{ fontFamily: monoFont, fontSize: 10, color: '#9ca3af', flexShrink: 0, pt: '2px', minWidth: 56 }}>
                                            {classified[idx]?.id}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: '#374151', lineHeight: 1.45 }}>
                                            {classified[idx]?.summary}
                                        </Typography>
                                    </Box>
                                ))}
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
});